import { useRouteLocale } from "@/hooks/useRouteLocale";
import { usePageMeta } from '@/hooks/usePageMeta';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';
import { Mail, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoginHeader } from '@/components/login';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import apiClient from '@/services/api/client';
import { PUBLIC_ROUTES } from '@/routes';

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = useRouteLocale();
  const currentLocale = locale || 'ro';

  usePageMeta({
    title: t("auth.forgotPassword.metaTitle"),
    description: t("auth.forgotPassword.metaTitle"),
    locale: locale,
    path: PUBLIC_ROUTES.forgotPassword(locale),
    noindex: true,
  });
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Create schema with translated messages
  const forgotPasswordSchema = z.object({
    email: z.string().min(1, t('validation.emailRequired')).email(t('validation.emailInvalid')),
  });

  type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setLoading(true);
      await apiClient.post('/auth/forgot-password', {
        user_email: data.email,
      });
      setEmailSent(true);
      toast.success(t('auth.forgotPassword.emailSent'));
    } catch (error: unknown) {
      const errorData = (error as { response?: { data?: { error?: { message_key?: string; code?: string; message?: string } } } })?.response?.data;
      let errorMessage = t('common.error');
      
      if (errorData?.error) {
        const errorKey = errorData.error.message_key || errorData.error.code || 'internal_error';
        const translatedError = t(`errors.${errorKey}`);
        if (translatedError && translatedError !== `errors.${errorKey}`) {
          errorMessage = translatedError;
        } else if (errorData.error.message) {
          errorMessage = errorData.error.message;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = () => {
    navigate(PUBLIC_ROUTES.login(currentLocale));
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <LoginHeader currentLocale={currentLocale} action={{ label: t('auth.signIn'), onClick: handleGoToLogin }} />
        
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md">
            <div className="bg-card border rounded-lg p-8 shadow-sm">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
                  <Mail className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
                <h1 className="text-2xl font-semibold text-foreground mb-2">
                  {t('auth.forgotPassword.checkEmail')}
                </h1>
                <p className="text-muted-foreground">
                  {t('auth.forgotPassword.emailSentDescription', { email: watch('email') })}
                </p>
              </div>

              <Button
                onClick={handleGoToLogin}
                className="w-full"
                variant="default"
              >
                {t('auth.goToLogin')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <LoginHeader currentLocale={currentLocale} action={{ label: t('auth.signIn'), onClick: handleGoToLogin }} />
      
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-card border rounded-lg p-8 shadow-sm">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-pink-100 dark:bg-pink-900/30 mb-4">
                <Mail className="h-8 w-8 text-pink-600 dark:text-pink-400" />
              </div>
              <h1 className="text-2xl font-semibold text-foreground mb-2">
                {t('auth.forgotPassword.title')}
              </h1>
              <p className="text-muted-foreground">
                {t('auth.forgotPassword.subtitle')}
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <Label htmlFor="email">
                  {t('auth.email')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder={t('auth.email')}
                  {...register('email')}
                  className="mt-1"
                />
                {errors.email && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
              >
                {loading ? t('common.loading') : t('auth.forgotPassword.sendResetLink')}
              </Button>
            </form>

            <div className="mt-6">
              <Button
                onClick={handleGoToLogin}
                className="w-full"
                variant="outline"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('auth.backToLogin')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

